import {makeAutoObservable, observable} from "mobx";
import {AxiosError} from "axios";
import {inject, injectable} from "inversify";
import {ViewController} from "data/types/structure";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import type {ILightPredictionsStore} from "data/stores/light_predictions/light_predictions.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType} from "data/enums";
import type {IUserStore} from "data/stores/user/user.store";
import type {IShareStore} from "data/stores/share/share.store";
import {extractErrorMessage} from "data/utils";
import {IApiResponse} from "data/services/http";
import {ConnextraType, createConnextraScript} from "data/utils/createConnextraScript";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";

export interface ISaveBarController extends ViewController {
	readonly i18n: ILocalizationStore;

	get isDisabled(): boolean;
	get isAllPredictionsDone(): boolean;
	get isPredictionsSaved(): boolean;
	get isLockout(): boolean;

	onSavePicks: () => Promise<void>;
}

@injectable()
export class SaveBarController implements ISaveBarController {
	@observable isLoading: boolean = false;

	get isAllPredictionsDone() {
		return this._lpStore.isAllPredictionsDone;
	}

	get isPredictionsSaved() {
		return this._lpStore.isPredictionsSaved;
	}

	get isDisabled() {
		return !this._lpStore.isPredictionsChanged || this.isLoading;
	}

	get isLockout() {
		return this._roundStore.isRoundsLockout;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ShareStore) private _shareStore: IShareStore
	) {
		makeAutoObservable(this);
	}

	onSavePicks = async () => {
		try {
			this.isLoading = true;

			const isAuthorized = this._userStore.isAuthorized;
			const modalType = isAuthorized ? ModalType.SAVE_PICKS : ModalType.LOSE_PICKS;

			if (this._userStore.isAuthorized) {
				await this._lpStore.savePredictions();
				void this._shareStore.sharePredictionImage().catch(console.error);
			}

			this._modalsStore.showModal(modalType, {
				message: "",
			});
		} catch (error) {
			this._modalsStore.showModal(ModalType.ERROR, {
				message: extractErrorMessage(error as AxiosError<IApiResponse>),
			});
		} finally {
			createConnextraScript(ConnextraType.PICK_CONFIRM, this._userStore.user?.id);
			this.isLoading = false;
		}
	};
}
